import {
  RESTRIKTION_CLASSES,
  RESTRIKTION_DESCRIPTION,
  RESTRIKTION_LABEL,
  worstRestriktionTyp,
  formatDate,
  formatRelativeTime,
  inForceRows,
  kommandeRows,
  type KommunStatus,
  type RestriktionTyp,
} from "@/lib/status";
import { ExternalLink, MapPin, CalendarDays, Info } from "lucide-react";

type Props = {
  kommunNamn: string;
  data: KommunStatus[];
  /** Whether this is a fallback / unknown lookup */
  unmatched?: boolean;
};

function RestriktionSection({ r }: { r: KommunStatus }) {
  const typ = r.typ;
  const kommande = r.period === "kommande";
  const cls = typ ? RESTRIKTION_CLASSES[typ] : RESTRIKTION_CLASSES.rekommendation;
  const hasDetails =
    r.datum_fran || r.datum_till || r.omraden_text || r.regler_text;
  return (
    <div className={`rounded-xl border ${cls.border} ${cls.bg} px-5 py-4${kommande ? " border-dashed" : ""}`}>
      <div className="flex items-center gap-2">
        <span
          className={`inline-block h-2.5 w-2.5 rounded-full ${cls.dot} ring-2 ring-background`}
          aria-hidden
        />
        <span className={`text-sm font-semibold ${cls.text}`}>
          {typ ? RESTRIKTION_LABEL[typ] : ""}
        </span>
        {kommande && (
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            Kommande
          </span>
        )}
        {r.omrade_nyckel && (
          <span className="ml-1 text-xs text-muted-foreground">
            · {r.omrade_nyckel}
          </span>
        )}
      </div>
      {hasDetails && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {(r.datum_fran || r.datum_till) && (
            <div className="flex gap-2">
              <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Period
                </div>
                <div className="mt-0.5 text-sm text-foreground">
                  {r.datum_fran ? formatDate(r.datum_fran) : "Okänt startdatum"}
                  {" – "}
                  {r.datum_till ? formatDate(r.datum_till) : "tills vidare"}
                </div>
              </div>
            </div>
          )}
          {r.omraden_text && (
            <div className="flex gap-2">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Områden
                </div>
                <p className="mt-0.5 text-sm text-foreground">{r.omraden_text}</p>
              </div>
            </div>
          )}
          {r.regler_text && (
            <div className="sm:col-span-2 flex gap-2">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Vad gäller
                </div>
                <p className="mt-0.5 text-sm text-foreground whitespace-pre-line">
                  {r.regler_text}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
      {r.kalla_url && (
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {formatRelativeTime(r.senast_scrapad)}
          </span>
          <a
            href={r.kalla_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          >
            Öppna källa <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}
    </div>
  );
}

export function StatusCard({ kommunNamn, data, unmatched }: Props) {
  const inForce = inForceRows(data);
  const kommande = kommandeRows(data);
  const worst = worstRestriktionTyp(
    inForce.map((r) => r.typ).filter((t): t is RestriktionTyp => t !== null)
  );
  const cls = worst ? RESTRIKTION_CLASSES[worst] : RESTRIKTION_CLASSES.rekommendation;
  const first = data[0] ?? null;

  const hasSource = data.some((r) => r.kalla_id && r.senast_scrapad);

  const headline = worst
    ? RESTRIKTION_LABEL[worst]
    : hasSource
      ? "Inga restriktioner"
      : "Ingen data tillgänglig";

  const kommandeWorst = worstRestriktionTyp(
    kommande.map((r) => r.typ).filter((t): t is RestriktionTyp => t !== null)
  );
  const kommandeFran = kommande
    .map((r) => r.datum_fran)
    .filter((d): d is string => d !== null)
    .sort()[0];

  let description = worst
    ? RESTRIKTION_DESCRIPTION[worst]
    : hasSource
      ? "Det finns inga aktiva restriktioner just nu enligt VA-huvudmannens information."
      : "Vi har inte kunnat bekräfta status för det här området. Vi gissar aldrig — kontrollera alltid med din VA-huvudman.";
  if (kommandeWorst) {
    description += ` Kommande: ${RESTRIKTION_LABEL[kommandeWorst].toLowerCase()}${
      kommandeFran ? ` från ${formatDate(kommandeFran)}` : ""
    }.`;
  }

  return (
    <article
      className={`overflow-hidden rounded-2xl border ${cls.border} bg-card shadow-sm`}
    >
      <div className={`${cls.bg} px-6 py-5 sm:px-8 sm:py-6`}>
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <MapPin className="h-4 w-4" />
          <span>{kommunNamn}</span>
          {first?.lan && <span className="text-muted-foreground/70">· {first.lan}</span>}
        </div>
        <div className="mt-3 flex items-center gap-3">
          <span
            className={`inline-block h-3 w-3 rounded-full ${cls.dot} ring-4 ring-background`}
            aria-hidden
          />
          <h2 className={`font-display text-3xl sm:text-4xl ${cls.text}`}>
            {headline}
          </h2>
        </div>
        <p className="mt-3 max-w-2xl text-base text-foreground/80">
          {description}
        </p>
      </div>

      <div className="px-6 py-6 sm:px-8 space-y-4">
        {data
          .filter((r) => r.restriktion_id)
          .filter(
            (r, i, arr) =>
              arr.findIndex((x) => x.restriktion_id === r.restriktion_id) === i
          )
          .map((r, i) => (
            <RestriktionSection key={r.restriktion_id ?? i} r={r} />
          ))}

        {unmatched && (
          <div className="flex gap-3">
            <Info className="mt-0.5 h-5 w-5 text-muted-foreground" />
            <p className="text-sm text-foreground/80">
              {kommunNamn} ingår inte i denna PoC ännu — vi gissar aldrig om status.
              Vi täcker just nu ett antal VA-källor och bygger ut listan löpande.
            </p>
          </div>
        )}
      </div>

      {first?.va_namn && (
        <div className="border-t border-border/60 bg-muted/30 px-6 py-4 sm:px-8">
          <div className="text-sm text-muted-foreground">
            Källa: <span className="text-foreground">{first.va_namn}</span>
            {first.va_webbplats && (
              <>
                {" · "}
                <a
                  href={first.va_webbplats}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
                >
                  {first.va_namn} <ExternalLink className="h-3 w-3" />
                </a>
              </>
            )}
          </div>
        </div>
      )}
    </article>
  );
}
